import { z } from 'zod';

/**
 * Schema for a single KYC field configuration entry.
 */
const FlowFieldSchema = z.object({
  key: z.string(),
  type: z.enum(['date', 'ssn', 'text', 'dropdown']),
  required: z.boolean(),
  order: z.number().optional(),
  modes: z.array(z.string()).optional()
});

/**
 * Generic params bag — the original query parameters that were converted into
 * the opaque session. Values are strings (as they arrive from the URL).
 * Only string values are accepted; anything else fails validation so malformed
 * data is never trusted.
 */
const ParamsSchema = z.record(z.string(), z.string());

/**
 * Schema for the Session Manager resolution response (GET /mobi/sessions/{id}).
 *
 * Validates all responses at the trust boundary before any data is used or
 * rendered. Responses that fail validation MUST be discarded.
 *
 * The session may carry data in two (non-exclusive) shapes:
 *   - `params`: the generic query-param bag (frontend auto-conversion path)
 *   - structured fields (branchId, processCodes, ...) from the bot worker path
 *
 * We require `status: 'active'` and a plausible opaque `sessionId`. Everything
 * else is optional/lenient so that ANY valid converted session resolves, while
 * still rejecting clearly malformed payloads (non-string params, wrong status).
 */
export const SessionResponseSchema = z.object({
  sessionId: z.string().min(22),
  params: ParamsSchema.optional(),
  branchId: z
    .string()
    .regex(/^A\d{5}$/)
    .nullable()
    .optional(),
  senderUUID: z.string().nullable().optional(),
  processCodes: z.array(z.enum(['ID', 'KY', 'KG', 'PD', 'VR'])).optional(),
  language: z.enum(['en', 'es', 'pt']).optional(),
  transactionId: z.string().nullable().optional(),
  transactionUuid: z.string().nullable().optional(),
  isBackCapture: z.boolean().optional(),
  isSelfieCapture: z.boolean().optional(),
  skipSelfieOnKyc: z.boolean().optional(),
  flowConfig: z
    .object({
      fields: z.array(FlowFieldSchema)
    })
    .nullable()
    .optional(),
  status: z.literal('active'),
  // One-time owner secret, present ONLY on the first (binding) resolve. Bound to
  // the first device that opened the link; used to unlock the customer's KYC
  // prefill on the KYC endpoint. Persisted client-side per session.
  ownerSecret: z.string().optional()
});

/**
 * Safely parse and validate a raw session response.
 * Returns { ok: true, data } on success, or { ok: false } on validation failure.
 * Never throws.
 */
export const parseSessionResponse = (raw) => {
  const result = SessionResponseSchema.safeParse(raw);
  if (result.success) {
    return { ok: true, data: result.data };
  }
  return { ok: false };
};

export default SessionResponseSchema;
